import React, { useState, useEffect, ReactNode, useRef } from "react";
import { Waypoint } from "react-waypoint";
import { useLocation } from "react-router-dom";
import { Table, Empty, Skeleton } from "antd";
import type { TableProps } from "antd";
import { useQuery, DocumentNode } from "@apollo/client";
import { cursorPagination, Localize } from "components/service";
import SkeletonCustom from "components/skeleton";
import { Loader } from "components/request-result";
import { useDebounce } from "components/use-hooks";
import { SorterResult } from "antd/lib/table/interface";

const SkeletonData = SkeletonCustom.TableData;

interface ITableMainCursorProps
{
  model: string;
  query: DocumentNode;
  querySkip?: boolean;
  autoLoadAll?: boolean;
  currentObj?: Record<string, any>;
  extraObj?: Record<string, any> | boolean;
  searchText?: string;
  routeUrl?: string;
  objectWhere?: Record<string, any>;
  tableHelper: {
    columns: any[];
    data: (model: any, currentObj: any, variables: any, language: string, extraObj?: any, pathname?: string) => any[];
  };
  children?: ReactNode;
  varSet?: {
    perPage?: number;
    [ key: string ]: any;
  };
  extraClass?: string;
  sortingName?: string | false;
  resetSorting?: { column: string; order: "ASC" | "DESC" }[];
  setVariables?: (variables: any) => void;
  setDataModel?: (model: any) => void;
  objOrderBy?: { column: string; order: "ASC" | "DESC" }[];
  tableLayout?: "auto" | "fixed";
  viewMode?: "table" | "grid";
  viewGrid?: React.ComponentType<any>;
  language?: string;
  expandable?: TableProps<any>["expandable"];
  rowClassName?: TableProps<any>["rowClassName"];
  emptyView?: ReactNode;
}

const TableMainCursor: React.FC<ITableMainCursorProps> = ({
  model,
  query,
  querySkip = false,
  autoLoadAll = false,
  currentObj,
  extraObj,
  searchText,
  objectWhere = {},
  tableHelper,
  children,
  varSet,
  extraClass = "",
  sortingName = false,
  resetSorting = false,
  setVariables = () => { },
  setDataModel = () => { },
  objOrderBy = [ { column: "ID", order: "DESC" } ],
  tableLayout = "fixed",
  viewMode = "table",
  viewGrid = () => null,
  language = "de",
  expandable,
  rowClassName,
  emptyView,
}) =>
{
  const { pathname } = useLocation();
  const perPage = varSet?.perPage ?? 50;
  const [ orderBy, setOrderBy ] = useState(objOrderBy);
  const autoLoadPending = useRef(false);

  const variables = {
    text: useDebounce(searchText),
    first: perPage,
    where: objectWhere,
    orderBy,
    ...(varSet ?? undefined),
  };

  const { data, loading, fetchMore } = useQuery(query, {
    skip: querySkip,
    variables: { ...variables },
    fetchPolicy: "cache-and-network",
    nextFetchPolicy: "cache-first",
    notifyOnNetworkStatusChange: autoLoadAll,
  });

  const { fetchMoreAction, pageInfo } = cursorPagination({
    model,
    data,
    loading,
    fetchMore,
  });

  const modelData = data?.[ model ]?.edges?.map((edge: any) => edge.node);
  const GridView = viewGrid;

  useEffect(() => {
    if (
      !autoLoadAll ||
      querySkip ||
      loading ||
      !pageInfo?.hasNextPage ||
      autoLoadPending.current
    ) {
      return;
    }

    autoLoadPending.current = true;
    fetchMoreAction().finally(() => {
      autoLoadPending.current = false;
    });
  }, [autoLoadAll, querySkip, loading, pageInfo?.hasNextPage, pageInfo?.endCursor]);

  /*eslint-disable */
  useEffect(() =>
  {
    setVariables(variables);
    setDataModel(modelData);
  }, [ data ]);

  useEffect(() =>
  {
    if (resetSorting) setOrderBy([]);
  }, [ resetSorting ]);
  /*eslint-enable */

  const EmptyData = () => (
    <div className="no-data-box">
      <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
      <span className="no-data-text">
        <Localize>GLOBAL.Text_NoData</Localize>
      </span>
    </div>
  );

  const initialLoading = autoLoadAll ? loading && !data : loading;
  const dataSource = initialLoading
    ? SkeletonData(tableHelper.columns, perPage, "my-2")
    : tableHelper.data(modelData, currentObj, variables, language, extraObj, pathname);
  const tableDataKey = expandable?.defaultExpandAllRows
    ? dataSource.map((row: any) => row.key).join("|")
    : undefined;
  const showEmptyView = Boolean(emptyView) &&
    !loading &&
    !variables.text?.trim() &&
    !modelData?.length;

  return (
    <>
      {showEmptyView ? (
        emptyView
      ) : (
        <>
          {children}

          {viewMode === "table" ? (
            <Table
              key={tableDataKey}
              className={`table-main ${extraClass}`}
              locale={{
                emptyText: <EmptyData /> as any,
                triggerDesc: <Localize>TABLES.Column_Sorting_Tooltip_Descending</Localize> as any,
                triggerAsc: <Localize>TABLES.Column_Sorting_Tooltip_Ascending</Localize> as any,
                cancelSort: <Localize>TABLES.Column_Sorting_Tooltip_Cancel</Localize> as any,
              }}
              dataSource={dataSource}
              columns={tableHelper.columns}
              tableLayout={tableLayout}
              pagination={false}
              expandable={expandable}
              rowClassName={rowClassName}
              onChange={(pagination, filters, sorter) => {
                const { column, order } = sorter as SorterResult<any>;
                if (column && order) {
                  const orderBy = [
                    {
                      column: (column as any).columnIndex,
                      order: order === "ascend" ? "ASC" : "DESC" as "ASC" | "DESC",
                    },
                  ];


                  setOrderBy(orderBy);
                  sortingName &&
                    localStorage.setItem(sortingName, JSON.stringify(orderBy));
                }
              }}
            />
          ) : initialLoading ? (
            <Skeleton active paragraph={{ rows: 20 }} />
          ) : modelData?.length ? (
            <GridView
              model={modelData}
              loading={loading}
              currentObj={currentObj}
              variables={variables}
              language={language}
              extraObj={extraObj}
            />
          ) : (
            <EmptyData />
          )}

          {pageInfo?.hasNextPage && (
            <>
              <Loader style={{ marginTop: "30px" }} type="block" />
              {!autoLoadAll && <Waypoint onEnter={() => fetchMoreAction()} />}
            </>
          )}
        </>
      )}
    </>
  );
};

export default TableMainCursor;
